import { useState } from "react";
import { Loader2, Search } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { type PagedOptionsSource, usePagedOptions } from "@/hooks/use-paged-options";
import type { OptionItem } from "@/lib/list-query";

interface LazyChecklistProps extends PagedOptionsSource {
  /** What is ticked, with names: a ticked option can be on no loaded page. */
  value: OptionItem[];
  onChange: (value: OptionItem[]) => void;
  /** False parks every request — for a source whose scope is not known yet. */
  enabled?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

/**
 * The multi-select cousin of `LazyCombobox`, for a filter panel: a checkbox
 * list over a list too long to ship whole. It searches on the server and loads
 * the next page as the list is scrolled.
 *
 * Ticked options that are on no loaded page — picked under another search —
 * are listed first, so every tick stays visible and can be taken off. One that
 * is on a loaded page stays where it is, so ticking does not move the row out
 * from under the pointer.
 */
export function LazyChecklist({
  value,
  onChange,
  queryKey,
  fetchPage,
  enabled = true,
  searchPlaceholder = "Search…",
  emptyMessage = "No results found.",
}: LazyChecklistProps) {
  const [searchInput, setSearchInput] = useState("");

  const { options, isFetching, hasNextPage, isError, listRef, endRef } = usePagedOptions({
    queryKey,
    fetchPage,
    search: searchInput,
    enabled,
  });

  const ticked = new Set(value.map((option) => option.id));
  const loaded = new Set(options.map((option) => option.id));
  const rows = [...value.filter((option) => !loaded.has(option.id)), ...options];

  const toggle = (option: OptionItem) =>
    onChange(
      ticked.has(option.id)
        ? value.filter((picked) => picked.id !== option.id)
        : [...value, option],
    );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-8 bg-background pl-8"
        />
      </div>

      <div
        ref={listRef}
        className="flex max-h-48 flex-col gap-2 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full"
      >
        {rows.map((option) => (
          <label key={option.id} className="flex items-center gap-1.5 cursor-pointer text-sm">
            <Checkbox checked={ticked.has(option.id)} onCheckedChange={() => toggle(option)} />
            <span className="truncate">{option.name}</span>
          </label>
        ))}

        {hasNextPage && <div ref={endRef} aria-hidden className="h-px shrink-0" />}

        {isFetching && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading…
          </div>
        )}

        {isError && !isFetching && (
          <p className="text-xs text-destructive">Couldn't load the list. Try again.</p>
        )}

        {!isFetching && !isError && rows.length === 0 && (
          <p className="text-xs text-muted-foreground">{emptyMessage}</p>
        )}
      </div>
    </div>
  );
}
